import {
  computed,
  makeObservable,
  observable,
  ObservableMap,
  ObservableSet,
  runInAction,
  toJS,
  action,
} from "mobx";

import { SpaceObservable } from "@/store/space/observables/SpaceObservable";
import { api } from "@/modules/api";
import { CacheKey } from "@/modules/cache/constants";
import { BaseError, RuntimeAssertionError } from "@/domains/errors";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { Uuid } from "@/domains/global/identifiers";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { logger } from "@/modules/logger";

export class AppStoreSpaceStore extends AppSubStore {
  spaceMap: ObservableMap<Uuid, SpaceObservable>;
  fetchMySpacesState: AsyncResult<ObservableSet<Uuid>>;
  /**
   * Advances for each fetch and reset so stale async work cannot restore state or cache data.
   */
  private fetchGeneration = 0;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.spaceMap = new ObservableMap();
    this.fetchMySpacesState = asyncResultModule.setInitial();

    makeObservable<AppStoreSpaceStore, "fetchGeneration">(this, {
      spaceMap: observable,
      fetchMySpacesState: observable,
      fetchGeneration: false,
      activeSpaceId: computed,
      myPersonalSpaceId: computed,
      getSpace: false,
      fetchMySpaces: action,
      persistMySpacesToCache: action,
      hydrateMySpacesFromCache: action,
      initialize: action,
      mySpaceIds: computed,
      mySpaces: computed,
      resetState: action,
    });
  }

  get activeSpaceId(): Uuid {
    return this.myPersonalSpaceId;
  }

  get myPersonalSpaceId(): Uuid {
    const myAccountId = this.store.account.myAccountId;

    const personalSpaceId = uuidModule.generatePersonalSpaceIdFromAccountId({
      accountId: myAccountId,
    });

    return personalSpaceId;
  }

  getSpace = (spaceId: Uuid): SpaceObservable | undefined => {
    return this.spaceMap.get(spaceId);
  };

  fetchMySpaces = async () => {
    const generation = ++this.fetchGeneration;

    runInAction(() => {
      this.fetchMySpacesState = asyncResultModule.setLoading();
    });

    try {
      const result = await api.get("/v1/me/spaces", {});

      if (generation !== this.fetchGeneration) {
        return;
      }

      if (!result.data) {
        throw new BaseError({
          message: "Failed to fetch spaces.",
          info: {
            result: objectModule.safeAsJson(result),
          },
        });
      }

      const spaceDataArray = result.data;
      if (spaceDataArray.length === 0) {
        throw new BaseError({ message: "Failed to fetch spaces: response was empty." });
      }

      const spaces = spaceDataArray.map(
        spaceData =>
          new SpaceObservable({
            id: spaceData.id,
            title: spaceData.title,
          })
      );

      runInAction(() => {
        spaces.forEach(space => {
          this.spaceMap.set(space.id, space);
        });

        const spaceIds = spaces.map(space => space.id);
        this.fetchMySpacesState.data = new ObservableSet(spaceIds);
      });

      await this.persistMySpacesToCache(spaces);
    } catch (unknownErr) {
      if (generation !== this.fetchGeneration) {
        return;
      }

      const err =
        unknownErr instanceof BaseError
          ? unknownErr
          : new BaseError({
              message: "Failed to fetch spaces.",
              originalError: unknownErr as Error,
            });

      runInAction(() => {
        this.fetchMySpacesState.error = err;
      });

      logger.warn({
        message: "[SpaceStore] Failed to refresh spaces.",
        info: { err: objectModule.safeErrorAsJson(err) },
      });
    } finally {
      if (generation === this.fetchGeneration) {
        runInAction(() => {
          this.fetchMySpacesState.loading = false;
        });
      }
    }
  };

  persistMySpacesToCache = async (spaces: SpaceObservable[]) => {
    try {
      await this.store.accountCache.set(
        CacheKey.MySpaces,
        spaces.map(space => toJS(space))
      );
    } catch (unknownErr) {
      logger.warn({
        message: "[SpaceStore] Failed to persist refreshed spaces.",
        info: { err: objectModule.safeErrorAsJson(unknownErr as Error) },
      });
    }
  };

  hydrateMySpacesFromCache = async (
    generation: number = this.fetchGeneration
  ): Promise<boolean> => {
    const mySpaces = await this.store.accountCache.get(CacheKey.MySpaces);

    if (generation !== this.fetchGeneration || !mySpaces) {
      return false;
    }

    const spaces = mySpaces.map(spaceData => {
      const space = new SpaceObservable({
        id: spaceData.id,
        title: spaceData.title,
      });

      return space;
    });

    runInAction(() => {
      spaces.forEach(space => {
        this.spaceMap.set(space.id, space);
      });

      const spaceIds = spaces.map(space => space.id);

      this.fetchMySpacesState.data = new ObservableSet(spaceIds);
    });

    return true;
  };

  initialize = async () => {
    const fetchMySpacesPromise = this.fetchMySpaces();
    const generation = this.fetchGeneration;
    const hydrationSuccess = await this.hydrateMySpacesFromCache(generation);

    if (generation !== this.fetchGeneration) {
      return;
    }

    /**
     * If we were able to hydrate from cache, we don't need to wait for the API call.
     */
    if (hydrationSuccess) {
      return;
    }

    await fetchMySpacesPromise;

    if (this.fetchMySpacesState.error) {
      throw new RuntimeAssertionError({
        message: "[SpaceStore.initialize] Failed.",
        info: { originalError: objectModule.safeErrorAsJson(this.fetchMySpacesState.error) },
      });
    }
  };

  get mySpaceIds(): Uuid[] {
    const spaceIds = this.fetchMySpacesState.data;

    if (!spaceIds) {
      throw new RuntimeAssertionError({
        message:
          "[SpaceAccountStore.spaceIds] spaceIds was not set - make sure that `initialize` was called.",
      });
    }

    if (!spaceIds.size) {
      throw new RuntimeAssertionError({
        message:
          "[SpaceAccountStore.spaceIds] spaceIds was empty - make sure that `initialize` was called.",
      });
    }

    return Array.from(spaceIds);
  }

  get mySpaces(): SpaceObservable[] {
    const spaces = this.mySpaceIds.map(spaceId => {
      const space = this.getSpace(spaceId);

      if (!space) {
        throw new RuntimeAssertionError({
          message:
            "[SpaceStore.mySpaces] space was not set - make sure that `initialize` was called.",
        });
      }

      return space;
    });

    return spaces;
  }

  resetState = () => {
    this.fetchGeneration += 1;

    runInAction(() => {
      this.spaceMap = new ObservableMap();
      this.fetchMySpacesState = asyncResultModule.setInitial();
    });
  };
}
